import React, { useRef, useEffect, type SyntheticEvent } from "react";
import { useFocusContext } from "~/contexts/FocusContext";
import { useSettings } from "~/contexts/useSettings";
import { Button } from "~/widgets/Button/Button";
import styles from "~/widgets/SettingsPopup/SettingsPopup.module.scss";

export interface SettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * SettingsPopup Component
 *
 * A modal dialog for configuring application settings.
 * Uses the native <dialog> element for accessibility.
 *
 * Features:
 * - Display settings (animate stat changes)
 * - ESC to close, backdrop click to close
 * - Room for future settings
 */
const SettingsPopup: React.FC<SettingsPopupProps> = ({ isOpen, onClose }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { settings, updateDisplaySettings } = useSettings();
  const { setIsOverlayOpen } = useFocusContext();

  // Notify FocusContext when overlay opens/closes
  useEffect(() => {
    setIsOverlayOpen(isOpen);
  }, [isOpen, setIsOverlayOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (isOpen && dialog && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog?.open) {
      dialog.close();
    }

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    if (dialog) {
      dialog.addEventListener("cancel", handleCancel);
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("cancel", handleCancel);
      }
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: SyntheticEvent) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const handleToggleAnimateStatChanges = () => {
    updateDisplaySettings({
      animateStatChanges: !settings.display.animateStatChanges,
    });
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClick={handleBackdropClick}
    >
      <div
        className={styles.dialogContent}
        onClick={(e: SyntheticEvent) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 className={styles.title}>Settings</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close settings"
          >
            &times;
          </button>
        </header>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Display</h3>

          <label className={styles.settingRow}>
            <span className={styles.settingLabel}>
              <span className={styles.settingName}>
                Animate stat changes
              </span>
              <span className={styles.settingDescription}>
                Show a flash animation when HP or Resilience values change
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={settings.display.animateStatChanges}
              className={`${styles.toggle} ${settings.display.animateStatChanges ? styles.toggleOn : ""
              }`}
              onClick={handleToggleAnimateStatChanges}
            >
              <span className={styles.toggleThumb} />
            </button>
          </label>
        </div>

        <div className={styles.footer}>
          <Button type="button" onClick={onClose} variant="primary" size="sm">
            Done
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default SettingsPopup;
